import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Store } from 'components/common/types/Store.types';
import magicWandSvg from 'assets/icons/magic-wand.svg';
import { Color } from 'lib/Button';
import { cancelFunctions } from 'utils/cancelFunctions';
import { initialiseThoughtspot } from 'utils/thoughtspotInit';
import DropdownMenu from 'lib/DropdownMenu';
import { DropdownMenuOption } from 'lib/DropdownMenu/DropdownMenu.types';
import { RevenueInsightsLiveboard } from './RevenueInsightsLiveboard/RevenueInsightsLiveboard';
import useHasInsightsAccess from '../InsightsAccess';
import { LabsLiveboardProps } from './LabsLiveboard.types';
import { InventoryReservationLiveboard } from './InventoryReservationLiveboard/InventoryReservationLiveboard';
import { POPLiveMonitoringLiveboard } from './POPLiveMonitoringLiveboard/POPLiveMonitoringLiveboard';
import { ProofOfPlayChinaLiveboard } from './ProofOfPlayChinaLiveboard/ProofOfPlayChinaLiveboard';
import { DealHealthChinaLiveboard } from './DealHealthChinaLiveboard/DealHealthChinaLiveboard';
import { DealHealthUSLiveboard } from './DealHealthUSLiveboard/DealHealthUSLiveboard';

type Option = {
  id: string;
  label: string;
  isVisible?: boolean;
  liveboard?: React.FC<LabsLiveboardProps>;
};

export const LabsReportDropdown: React.FC = () => {
  const { hasAccess } = useHasInsightsAccess();
  const { localeCode } = useSelector((state: Store) => state.publisher.configuration);

  const [visibleLiveboard, setVisibleLiveboard] = useState('');

  const hideLiveboard = (): void => setVisibleLiveboard('');

  initialiseThoughtspot(localeCode, cancelFunctions);

  const labsLiveboardOptions: Option[] = [
    {
      id: 'deal-health-china',
      label: 'Deal health',
      liveboard: DealHealthChinaLiveboard,
      isVisible: hasAccess.labs.dealHealthChina,
    },
    {
      id: 'deal-health-us',
      label: 'Deal health 2.0',
      liveboard: DealHealthUSLiveboard,
      isVisible: hasAccess.labs.dealHealthUS,
    },
    {
      id: 'proof-of-play-china',
      label: 'Proof of play',
      liveboard: ProofOfPlayChinaLiveboard,
      isVisible: hasAccess.labs.proofOfPlayChina,
    },
    {
      id: 'pop-live-monitoring',
      label: 'POP Live Monitoring',
      liveboard: POPLiveMonitoringLiveboard,
      isVisible: hasAccess.labs.popLiveMonitoring,
    },
    {
      id: 'revenue-insights',
      label: 'Revenue insights',
      liveboard: RevenueInsightsLiveboard,
      isVisible: hasAccess.labs.revenueInsights,
    },
    {
      id: 'inventory-reservation',
      label: 'Inventory reservation',
      liveboard: InventoryReservationLiveboard,
      isVisible: hasAccess.labs.inventoryReservation,
    },
  ];

  const labsReportDropdownOptions: DropdownMenuOption[] = labsLiveboardOptions
    .filter((option) => option.isVisible)
    .map((option) => ({
      ...option,
      onClick: () => setVisibleLiveboard(option.id),
    }));

  return (
    <>
      <DropdownMenu
        dropdownMenuOptions={labsReportDropdownOptions}
        icon={magicWandSvg}
        label="LABS"
        color={Color.PRIMARY}
      />

      {labsLiveboardOptions.map(
        ({ id, liveboard: Liveboard }) =>
          !!Liveboard && <Liveboard key={id} showLiveboard={visibleLiveboard === id} onHideLiveboard={hideLiveboard} />,
      )}
    </>
  );
};
